const en = {
  common: {
    loading: 'Loading...',
    on: 'On',
    off: 'Off',
    error: 'Something went wrong',
    retry: 'Try again',
    cancel: 'Cancel',
    done: 'Done',
    today: 'Today',
    yesterday: 'Yesterday',
    noResults: 'No results',
    copied: 'Copied',
    stampCount: '{{current}} / {{total}} stamps',
    activity: {
      type: {
        stamps_added: 'Stamp earned',
        stamp_correction: 'Stamp adjustment',
        reward_unlocked: 'Reward unlocked',
        reward_redeemed: 'Reward redeemed',
        reward_restored: 'Reward adjustment',
        referral_bonus: 'Referral bonus',
        reward_expired: 'Reward expired',
        new_member_joined: 'New member joined',
        birthday_reward_unlocked: 'Birthday reward unlocked',
      },
      detail: {
        spendBased: '{{amount}} SEK per stamp',
        perMeal: 'Per meal',
        referralBonus: 'Referral bonus',
        byActor: 'By {{actor}}',
        byActorWithItem: '{{item}} · By {{actor}}',
        targetReduction: 'Stamp target reduced · {{targetBefore}} → {{targetAfter}} per card',
      },
      actor: {
        manager: 'Manager',
        admin: 'Admin',
        cashier: 'Cashier',
      },
      stampSingle: 'stamp',
      stamps: 'stamps',
      rewardSingle: 'reward',
      rewards: 'rewards',
    },
    adjustmentReasons: {
      customer_complaint: 'Customer complaint',
      system_error: 'System error',
      cashier_mistake: 'Cashier mistake',
      promotion: 'Promotion / goodwill',
      other: 'Other',
    },
  },
  error: {
    title: 'Something went wrong',
    subtitle: "We couldn't complete your request.\nPlease try again.",
    tryAgain: 'Try again',
    footer: 'If this keeps happening, please try again later.',
    imageAccessibilityLabel: 'Error icon',
  },
  placeholder: {
    cashierReady: 'Cashier app is ready.',
    adminReady: 'Admin dashboard is ready.',
  },
  adminDashboard: {
    homeTitle: 'Home',
    homeSubtitle: 'Overview of your loyalty program activity.',
    home: {
      chart: {
        title: 'Loyalty trends',
        subtitle: 'Track key loyalty metrics over time.',
      },
      totalMembers: {
        label: 'Total members',
        allTime: 'All-time',
      },
      stats: {
        stampsAdded: 'Stamps added',
        rewardsRedeemed: 'Rewards redeemed',
        rewardsUnlocked: 'Rewards unlocked',
        newMembers: 'New members',
        vsPrevDays: 'vs prev. {{count}} days',
      },
    },
    membersTitle: 'Members',
    membersSubtitle: 'Search for a member and view or adjust their stamps and rewards.',
    recentActivityTitle: 'Recent activity',
    recentActivitySubtitle: 'Review loyalty activity across members and staff.',
    auditLogTitle: 'Audit log',
    auditLogSubtitle: 'Track admin and system changes.',
    loyaltyTitle: 'Loyalty',
    loyaltySubtitle: 'Configure your loyalty program and rewards.',
    birthdayRewardTitle: 'Birthday Reward',
    birthdayRewardSubtitle: 'Configure the birthday reward programme.',
    operationsTitle: 'Operations',
    operationsSubtitle: 'Manage cashiers, devices, and branches.',
    settingsTitle: 'App settings',
    settingsSubtitle: 'Configure app and program options.',
  },
  operations: {
    actions: {
      new: 'New',
      addCashier: 'Add cashier',
      registerDevice: 'Register device',
    },
    cashiers: {
      title: 'Cashiers',
      description: 'Staff and manager PIN access for cashier devices.',
      columns: {
        cashier: 'Cashier',
        role: 'Role',
        status: 'Status',
        lastActivity: 'Last activity',
      },
      editAccessibilityLabel: 'Edit cashier {{name}}',
      emptyTitle: 'No cashiers yet',
      emptyDescription: 'Add cashiers to give staff and managers PIN access to cashier devices.',
      loadError: 'Could not load cashiers. Please try again.',
      noResults: 'No cashiers match the selected filters.',
    },
    devices: {
      title: 'Devices',
      description: 'Registered devices used by cashiers.',
      columns: {
        device: 'Device',
        branch: 'Branch',
        status: 'Status',
        lastActivity: 'Last activity',
      },
      editAccessibilityLabel: 'Edit device {{name}}',
      emptyTitle: 'No devices registered',
      emptyDescription: 'Register devices so cashiers can process stamps and rewards.',
      loadError: 'Could not load devices. Please try again.',
      noResults: 'No devices match the selected filters.',
    },
    roles: {
      staff: 'Staff',
      manager: 'Manager',
    },
    statuses: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
    },
    filters: {
      label: 'Filter',
      clear: 'Clear filters',
    },
    pagination: {
      range: 'Showing {{start}}-{{end}} of {{total}}',
      previous: 'Previous',
      next: 'Next',
    },
    addDevice: {
      title: 'Add device',
      closeAccessibilityLabel: 'Close add device',
      deviceNameLabel: 'Device name',
      deviceNamePlaceholder: 'e.g. Front Counter Tablet',
      deviceNameRequiredError: 'Enter a device name',
      branchLabel: 'Branch',
      branchPlaceholder: 'Select branch',
      branchRequiredError: 'Select a branch',
      submitButton: 'Add device',
      submittingButton: 'Adding...',
      successTitle: 'Device added',
      successDescription: 'Pair this device with the cashier app to finish setup.',
      pairingCodeLabel: 'Pairing code',
      pairingCodeOneTimeNote: 'Copy this code now — it will not be shown again.',
      pairingCodeExpiresAt: 'Expires at {{time}} today',
      pairingCodeExpiresAtTomorrow: 'Expires at {{time}} tomorrow',
      copyCodeButton: 'Copy code',
      copiedCodeButton: 'Copied!',
      copyCodeAccessibilityLabel: 'Copy pairing code',
      nameConflictError: 'A device with that name already exists in this branch.',
      submitError: 'Something went wrong. Please try again.',
      addAnotherButton: 'Add another',
    },
    editDevice: {
      title: 'Edit device',
      subtitle: 'Update device details and manage device status.',
      closeAccessibilityLabel: 'Close edit device',
      statusLabel: 'Status',
      pendingTitle: 'Pending pairing',
      pendingDescription: 'This device has not been paired yet.',
      pairingCodeUnavailablePrefix: 'Pairing code unavailable. ',
      pairingCodeExpiredPrefix: 'Pairing code expired. ',
      pairingCodeRegenerateLink: 'Regenerate to get a new one.',
      copyCodeAccessibilityLabel: 'Copy pairing code',
      regenerateCodeButton: 'Regenerate code',
      regeneratingCodeButton: 'Regenerating...',
      regenerateCodeError: 'Could not regenerate code. Please try again.',
      reactivateButton: 'Reactivate',
      reactivatingButton: 'Reactivating...',
      reactivateError: 'Could not reactivate device. Please try again.',
      makeInactiveButton: 'Make inactive',
      makingInactiveButton: 'Deactivating...',
      statusChangeError: 'Could not update device status. Please try again.',
      inactiveNote: 'Inactive devices cannot process stamps or rewards.',
      activeNote: 'This device is paired and ready to process stamps and rewards.',
      pairingCodeExpiredDescription: 'This device has not been paired yet. Generate a new code to pair this device.',
      generateNewCodeButton: 'Generate new code',
      generatingNewCodeButton: 'Generating...',
      savedLabel: 'Saved',
      submitButton: 'Save changes',
      savingButton: 'Saving...',
      nameConflictError: 'A device with that name already exists in this branch.',
      reactivateNameConflictError: 'Another active device already has this name in this branch. Rename it first, then reactivate.',
      reactivateStateConflictError: 'This device\'s status already changed elsewhere. Close and reopen it to see the latest status.',
      submitError: 'Something went wrong. Please try again.',
    },
    addCashier: {
      title: 'Add cashier',
      closeAccessibilityLabel: 'Close add cashier',
      nameLabel: 'Cashier name',
      namePlaceholder: 'e.g. Emma Anderson',
      nameRequiredError: 'Enter a name',
      emailLabel: 'Email (optional)',
      emailPlaceholder: 'e.g. emma@chillout.com',
      emailInvalidError: 'Enter a valid email address',
      roleLabel: 'Role',
      submitButton: 'Create cashier',
      submittingButton: 'Creating...',
      submitError: 'Something went wrong. Please try again.',
      pinTitle: 'Cashier added',
      pinDescription: 'Share this PIN with the cashier. They can use it straight away and update it at any time.',
      pinCopyButton: 'Copy PIN',
      pinCopiedButton: 'Copied!',
    },
    editCashier: {
      title: 'Edit cashier',
      subtitle: 'Update cashier details and status.',
      closeAccessibilityLabel: 'Close edit cashier',
      nameLabel: 'Full name',
      namePlaceholder: 'e.g. Emma Anderson',
      nameRequiredError: 'Enter a name',
      emailLabel: 'Email (optional)',
      emailPlaceholder: 'e.g. emma@chillout.com',
      emailInvalidError: 'Enter a valid email address',
      roleLabel: 'Role',
      statusLabel: 'Status',
      inactiveNote: 'Inactive cashiers cannot sign in to cashier devices.',
      makeInactiveButton: 'Make inactive',
      makingInactiveButton: 'Making inactive...',
      deactivateButton: 'Deactivate',
      deactivatingButton: 'Deactivating...',
      deactivateConfirmDescription: 'This cashier\'s PIN will stop working immediately.',
      deactivateError: 'Something went wrong. Please try again.',
      reactivateButton: 'Reactivate',
      reactivatingButton: 'Reactivating...',
      reactivatePinTitle: 'Cashier reactivated',
      statusChangeError: 'Something went wrong. Please try again.',
      pinAccessLabel: 'PIN access',
      pinAccessDescription: 'Cashier PIN is hidden for security. Reset it only if the cashier cannot sign in.',
      resetPinButton: 'Reset PIN',
      submitButton: 'Save changes',
      savingButton: 'Saving...',
      submitError: 'Something went wrong. Please try again.',
      resetPinInactiveNote: 'PIN reset is not available for inactive cashiers. Reactivate the cashier first.',
      resetPinTitle: 'Reset PIN',
      resetPinDescription: 'Reset this cashier\'s PIN?\nThe current PIN will stop working immediately.',
      resetPinBackButton: 'Back',
      resetPinConfirmButton: 'Reset PIN',
      resetPinResettingButton: 'Resetting...',
      resetPinError: 'Something went wrong. Please try again.',
      pinTitle: 'PIN reset',
      pinDescription: 'Share this new PIN with the cashier. Their previous PIN no longer works.',
      pinCopyButton: 'Copy PIN',
      pinCopiedButton: 'Copied!',
    },
  },
  members: {
    search: {
      label: 'Search',
      placeholder: 'Search by name, email, or member ID',
      helperText: 'Enter a name, email address, or member ID.',
      accessibilityLabel: 'Search members by name, email, or member ID',
      clearAccessibilityLabel: 'Clear search',
    },
    emptyState: {
      title: 'Search for a member',
      description: 'Enter a name, email address, or member ID above to find a member and manage their loyalty balance.',
    },
    results: {
      heading: 'Search results',
      count_one: '{{count}} member found',
      count_other: '{{count}} members found',
      noResults: 'No members match your search.',
    },
    columns: {
      member: 'Member',
      email: 'Email',
      stamps: 'Stamps',
      rewards: 'Rewards',
    },
    stampProgress: '{{current}} / {{target}} stamps',
    rewardCount: '{{count}} rewards',
    rowAccessibilityLabel: '{{name}}, {{current}} of {{target}} stamps, {{rewardCount}} rewards',
    detail: {
      backToResults: 'Back to results',
      memberSince: 'Member since {{date}}',
      anonymiseButton: 'Anonymise',
      anonymise: {
        confirmTitle: 'Anonymise {{name}}?',
        confirmBody: 'This will permanently remove all personal information for this member. This action cannot be undone.',
        confirmWarning: 'This action is permanent and cannot be undone.',
        emailConfirmLabel: 'Type the member email to confirm',
        emailConfirmPlaceholder: 'Enter member email',
        emailMismatch: 'Email does not match',
        confirmButton: 'Anonymise member',
        confirmingButton: 'Anonymising...',
        applyError: 'Could not anonymise member. Please try again.',
      },
      tabs: {
        stamps: 'Stamps',
        rewards: 'Rewards',
      },
      stampPreview: {
        title: 'Stamp preview',
        balance: '{{current}} / {{target}}',
        stampFilledAccessibilityLabel: 'Stamp collected',
        stampEmptyAccessibilityLabel: 'Empty stamp slot',
      },
      availableRewards: {
        title: 'Available rewards',
        expiresAt: 'Expires {{date}}',
        noExpiry: 'No expiry',
        empty: 'No available rewards.',
      },
      recentActivity: {
        title: 'Recent activity',
        viewAll: 'View all',
        viewAllAccessibilityLabel: 'View all member activity',
        empty: 'No activity yet.',
        activityType: {
          stamps_added: 'Stamps added',
          stamp_correction: 'Stamp correction',
          reward_unlocked: 'Reward unlocked',
          reward_redeemed: 'Reward redeemed',
          reward_restored: 'Reward restored',
          referral_bonus: 'Referral bonus',
          reward_expired: 'Reward expired',
        },
      },
      loadError: 'Could not load member details. Please try again.',
      stampAdjustment: {
        numberOfStamps: 'Number of stamps',
        stampCountSingle: '1 stamp',
        stampCount: '{{count}} stamps',
        willAdd: 'This will add {{count}} stamp',
        willAddPlural: 'This will add {{count}} stamps',
        willRemove: 'This will remove {{count}} stamp',
        willRemovePlural: 'This will remove {{count}} stamps',
        decrementAccessibilityLabel: 'Decrease stamp count',
        incrementAccessibilityLabel: 'Increase stamp count',
        countAccessibilityLabel: '{{count}} stamps selected',
        reason: 'Reason',
        reasonPlaceholder: 'Select a reason',
        reasonRequired: 'Select a reason',
        reasonOptions: {
          customer_complaint: 'Customer complaint',
          system_error: 'System error',
          cashier_mistake: 'Cashier mistake',
          promotion: 'Promotion / goodwill',
          other: 'Other',
        },
        note: 'Note (optional)',
        notePlaceholder: 'Add a note about this adjustment...',
        applyButton: 'Apply stamp adjustment',
        applyingButton: 'Applying...',
        confirmTitle_add: 'Add {{count}} stamps?',
        confirmTitle_remove: 'Remove {{count}} stamps?',
        confirmButton_add_one: 'Add {{count}} stamp',
        confirmButton_add_other: 'Add {{count}} stamps',
        confirmButton_remove_one: 'Remove {{count}} stamp',
        confirmButton_remove_other: 'Remove {{count}} stamps',
        confirmBody_add: 'You are about to add {{count}} stamps to this member.',
        confirmBody_remove: 'You are about to remove {{count}} stamps from this member.',
        confirmBefore: 'Before',
        confirmAfter: 'After',
        auditWarning: 'This adjustment will be recorded in the activity and audit logs.',
        applyError: 'Could not apply stamp adjustment. Please try again.',
      },
      rewardAdjustment: {
        rewardBalance: 'Reward balance',
        adjustQuantity: 'Adjust quantity',
        rewardCountSingle: '1 reward',
        rewardCount: '{{count}} rewards',
        decrementAccessibilityLabel: 'Decrease reward count',
        incrementAccessibilityLabel: 'Increase reward count',
        countAccessibilityLabel: '{{count}} rewards selected',
        reason: 'Reason',
        reasonPlaceholder: 'Select a reason',
        reasonOptions: {
          customer_complaint: 'Customer complaint',
          system_error: 'System error',
          cashier_mistake: 'Cashier mistake',
          promotion: 'Promotion / goodwill',
          other: 'Other',
        },
        note: 'Note (optional)',
        notePlaceholder: 'Add a note about this adjustment...',
        applyButton: 'Apply reward adjustment',
        applyingButton: 'Applying...',
        confirmTitle_add: 'Add {{count}} {{name}} rewards?',
        confirmTitle_remove: 'Remove {{count}} {{name}} rewards?',
        confirmButton_add_one: 'Add {{count}} reward',
        confirmButton_add_other: 'Add {{count}} rewards',
        confirmButton_remove_one: 'Remove {{count}} reward',
        confirmButton_remove_other: 'Remove {{count}} rewards',
        confirmBody_add: 'You are about to add {{count}} {{name}} rewards to this member.',
        confirmBody_remove: 'You are about to remove {{count}} {{name}} rewards from this member.',
        applyError: 'Could not apply reward adjustment. Please try again.',
        selectCardAccessibilityLabel: 'Select {{name}}',
      },
    },
    searchError: 'Could not search members. Please try again.',
  },
  adminAuth: {
    logoAccessibilityLabel: 'Chill Out',
    signInIconAccessibilityLabel: 'Admin account',
    signInPlaceholder: 'Admin sign in',
    signInSubtitle: 'Manage loyalty settings, cashiers, and devices.',
    emailLabel: 'Email',
    emailPlaceholder: 'Enter your email',
    emailRequiredError: 'Enter your email address',
    emailInvalidError: 'Enter a valid email address',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    passwordRequiredError: 'Enter your password',
    passwordTooShortError: 'Use at least 10 characters',
    showPasswordAccessibilityLabel: 'Show password',
    hidePasswordAccessibilityLabel: 'Hide password',
    forgotPassword: 'Forgot password?',
    signInError: "We couldn't sign you in. Check your email and password and try again.",
    signInButton: 'Sign in',
    signingInButton: 'Signing in...',
    forgotPasswordIconAccessibilityLabel: 'Password reset',
    forgotPasswordTitle: 'Forgot password?',
    forgotPasswordSubtitle: 'Enter your admin email and we’ll send you a link to reset your password.',
    sendResetLinkButton: 'Send reset link',
    sendingResetLinkButton: 'Sending...',
    resetLinkRequestError: "We couldn't send a reset link right now. Please try again later.",
    checkEmailIconAccessibilityLabel: 'Reset email sent',
    checkEmailTitle: 'Check your email',
    checkEmailSubtitle: "If this email belongs to an admin account, we'll send a password reset link.",
    checkEmailNoteIconAccessibilityLabel: 'Email resend reminder',
    checkEmailNoteTitle: "Didn't receive the email?",
    checkEmailNoteBody: 'Check your spam folder or try sending the link again.',
    resendTimerPrefix: 'You can resend the link in',
    resendReadyMessage: 'You can send the link again now',
    sendAgainButton: 'Send again',
    sendingAgainButton: 'Sending...',
    createPasswordIconAccessibilityLabel: 'New password',
    createPasswordTitle: 'Create new password',
    createPasswordSubtitle: 'Enter and confirm your new password to continue.',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'Enter your new password',
    newPasswordHelperText: 'Use at least 10 characters. Longer passwords are more secure.',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Confirm your new password',
    confirmPasswordRequiredError: 'Confirm your password',
    passwordsDoNotMatchError: 'Passwords do not match',
    updatePasswordButton: 'Update password',
    updatingPasswordButton: 'Updating...',
    samePasswordError: 'Your new password must be different from your current password.',
    updatePasswordError: "We couldn't update your password. Request a new reset link and try again.",
    resetLinkErrorIconAccessibilityLabel: 'Invalid reset link',
    resetLinkErrorTitle: 'Invalid or expired link',
    resetLinkErrorSubtitle: "This password reset link can't be used. Request a new link to continue.",
    requestNewLinkButton: 'Request new link',
    backToSignIn: 'Back to sign in',
  },
  cashierHeader: {
    logoAccessibilityLabel: 'Chill Out',
    menuButton: 'Device menu',
    backButton: 'Go back',
    changeCashierPinItem: 'Change cashier PIN',
    unpairItem: 'Unpair device',
    unpairConfirmTitle: 'Unpair this device?',
    unpairConfirmMessage: 'This device will be signed out and must be paired again from the admin dashboard before it can be used.',
    unpairCancelButton: 'Cancel',
    unpairConfirmButton: 'Unpair',
    unpairErrorTitle: 'Could not unpair device',
    unpairErrorMessage: 'Check your connection and try again.',
  },
  cashierChangePin: {
    currentPinTitle: 'Enter current PIN',
    currentPinSubtitle: 'Enter your current cashier PIN.',
    currentPinModalAccessibilityLabel: 'Enter current cashier PIN',
    newPinTitle: 'Set new PIN',
    newPinSubtitle: 'Choose a new 4-digit PIN.',
    newPinModalAccessibilityLabel: 'Set new cashier PIN',
    confirmPinTitle: 'Confirm new PIN',
    confirmPinSubtitle: 'Enter the new PIN again.',
    confirmPinModalAccessibilityLabel: 'Confirm new cashier PIN',
    currentPinInvalidError: 'Current PIN is incorrect.',
    newPinInvalidError: 'Enter a 4-digit PIN.',
    confirmPinMismatchError: 'PINs do not match. Try again.',
    updatePinNetworkError: 'Could not verify PIN. Check your connection and try again.',
    successTitle: 'PIN updated',
    successSubtitle: 'Your cashier PIN has been updated.',
    successModalAccessibilityLabel: 'Cashier PIN updated',
    successImageAccessibilityLabel: 'PIN updated illustration',
    doneButton: 'Done',
    closeModalAccessibilityLabel: 'Close cashier PIN change',
    pinProgressAccessibilityLabel: '{{entered}} of {{total}} PIN digits entered',
  },
  cashierPairing: {
    iconAccessibilityLabel: 'Pair device',
    title: 'Pair this device',
    subtitle: 'Scan the pairing QR code from the admin dashboard, or enter the 6-digit pairing code manually.',
    scanQRButton: 'Scan QR code',
    enterCodeButton: 'Enter code manually',
    codeScreenTitle: 'Enter pairing code',
    codeScreenSubtitle: 'Type the 6-digit pairing code from the admin dashboard.',
    scanQRInstead: 'Scan QR instead',
    scanQRScreenTitle: 'Scan pairing QR',
    scanQRScreenSubtitle: 'Point the camera at the pairing QR code from the admin dashboard.',
    scanInvalidError: 'This is not a valid pairing QR code.',
    cameraPermissionDescription: 'Camera access is needed to scan the pairing QR code.',
    cameraPermissionDeniedDescription: 'Camera access is off. Enable it in Settings to scan QR codes.',
    cameraPermissionButton: 'Allow camera access',
    cameraSettingsButton: 'Open Settings',
    codeFormatError: 'Enter the 6-digit pairing code from the admin dashboard.',
    codeInvalidError: 'Pairing code not found. Check the code or request a new one from the admin dashboard.',
    codeExpiredError: 'Pairing code expired. Request a new code from the admin dashboard.',
    codeNetworkError: 'Could not connect. Check your connection and try again.',
    codeServerError: 'Could not complete pairing. Check the server logs and try again.',
    codeProgressAccessibilityLabel: '{{entered}} of {{total}} pairing code digits entered',
    successImageAccessibilityLabel: 'Device paired',
    successTitle: '{{deviceName}} is paired to {{branchName}}.',
    successSubtitle: 'This device is now ready to use.',
    successContinue: 'Continue',
  },
  numpad: {
    deleteButton: 'Delete',
    confirmButton: 'Confirm',
    doubleZeroButton: 'Double zero',
  },
  cashierHome: {
    scannerTitle: 'Scan member or reward QR',
    scannerSubtitle: 'Point the camera at the customer\'s QR code.',
    scannerTitleAccessibilityLabel: 'Scanner status',
    cameraPermissionDescription: 'Camera access is needed to scan member and reward QR codes.',
    cameraPermissionDeniedDescription: 'Camera access is off. Enable it in Settings to scan QR codes.',
    cameraPermissionButton: 'Allow camera access',
    cameraSettingsButton: 'Open Settings',
    tapToResume: 'Tap to resume',
    idleAccessibilityLabel: 'Scanner paused. Tap to resume scanning.',
    cantScanHint: "Can't scan the QR?",
    manualLookupTitle: 'Manual lookup',
    manualLookupSubtitle: 'Find a member by entering their email or member ID.',
    searchPlaceholder: 'Search by email or member ID',
    searchAccessibilityLabel: 'Search by email or member ID',
    searchButton: 'Search',
    memberFound: 'Member found',
    memberFoundBackdropAccessibilityLabel: 'Close member card',
    memberFoundMenuAccessibilityLabel: 'More options',
    memberFoundUnknownName: 'Unknown member',
    scanInstead: 'Scan QR instead',
    scanInsteadAccessibilityLabel: 'Switch back to QR scanner',
    searchFormatError: 'Enter a valid email address or member ID (e.g. CO000000).',
    searchNotFoundError: 'No member found. Check the email or member ID and try again.',
    searchNetworkError: 'Could not connect. Check your connection and try again.',
    exactMatchOnly: 'Exact match only',
    exactMatchDescription: 'Enter the full email or member ID to protect customer privacy.',
    enterManually: 'Enter manually',
    enterManuallyAccessibilityLabel: 'Enter member details manually',
    scanErrorTitle: 'QR scan failed',
    scanErrorDismiss: 'OK',
    scanInvalidError: 'This QR code is not valid.',
    scanExpiredError: 'This QR code has expired. Ask the customer to refresh it.',
    scanMemberNotFoundError: 'No member found for this QR code.',
    scanAlreadyConsumedError: 'This reward has already been used.',
    scanNoLongerActiveError: 'This redemption code is no longer active.',
    scanNetworkError: 'Could not verify the QR code. Check your connection and try again.',
  },
  nav: {
    home: 'Home',
    members: 'Members',
    recentActivity: 'Recent activity',
    auditLog: 'Audit log',
    loyalty: 'Loyalty',
    birthdayReward: 'Birthday Reward',
    operations: 'Operations',
    settings: 'App settings',
    logOut: 'Log out',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    version: 'v{{version}}',
    updateAvailable: 'A new version is available.',
    updateMessage: 'Refresh to get the latest updates.',
    updateRefresh: 'Update now',
    updateDismissAccessibilityLabel: 'Dismiss update notification',
  },
  rewardRedemption: {
    sheetBackdropAccessibilityLabel: 'Close reward redemption card',
    rewardFound: 'Reward found',
    unknownMember: 'Unknown member',
    qrValid: 'QR code valid · {{countdown}}',
    qrExpired: 'QR code expired',
    rewardToRedeem: 'Reward to redeem',
    currentCount: 'Current',
    afterCount: 'After this',
    rewardWithQuantity: '{{name}} × {{count}}',
    insufficientRewardsWarning: 'QR is for {{requested}} rewards but member only has {{available}}.',
    continueButton: 'Continue',
    staffPin: {
      title: 'Cashier PIN',
      subtitle: 'Enter your PIN to confirm the redemption.',
      modalAccessibilityLabel: 'Cashier PIN confirmation',
      closeAccessibilityLabel: 'Cancel PIN entry',
      codeProgressAccessibilityLabel: '{{entered}} of {{total}} digits entered',
      incorrectPin: 'Incorrect PIN. Please try again.',
      redeemError: 'Could not redeem reward. Please try again.',
    },
    success: {
      title: 'Reward redeemed',
      usedSingle: '{{rewardName}} used for {{fullName}}.',
      used: '{{count}}× {{rewardName}} used for {{fullName}}.',
      remainingPrefix: '{{firstName}} now has',
      remainingSuffix: 'available {{rewardName}} rewards.',
      scanNext: 'Scan next',
      done: 'Done',
    },
  },
  rewardRestore: {
    title: 'Restore reward',
    noRewardItems: 'No reward items found.',
    rewardBalance: {
      title: 'Reward balance',
      count: '×{{count}}',
      pendingDelta: '+{{count}}',
      cardAccessibilityLabel: 'Select {{name}}, {{count}} available',
    },
    stepper: {
      label: 'Rewards to restore',
      rewardSingle: '+ 1 reward',
      rewards: '+ {{count}} rewards',
      decrementAccessibilityLabel: 'Restore fewer rewards',
      incrementAccessibilityLabel: 'Restore more rewards',
      countAccessibilityLabel: 'Restoring {{count}} rewards',
    },
    balancePreview: {
      current: 'Current:',
      available: '{{count}} available',
    },
    reason: {
      label: 'Reason for restore',
      placeholder: 'Select a reason',
    },
    note: 'Note (optional)',
    notePlaceholder: 'Add a note about this restore...',
    disclaimer: 'This action creates a correction record. Original activity remains unchanged.',
    restoreButton: 'Restore reward',
    managerPin: {
      title: 'Manager PIN',
      subtitle: 'Enter manager PIN to restore reward.',
      modalAccessibilityLabel: 'Manager PIN authorisation',
      closeAccessibilityLabel: 'Cancel PIN entry',
      codeProgressAccessibilityLabel: '{{entered}} of {{total}} digits entered',
      incorrectPin: 'Incorrect PIN. Please try again.',
      restoreError: 'Could not restore reward. Please try again.',
    },
    success: {
      title: 'Reward restored',
      rewardSingle: '1 {{name}} restored for {{fullName}}.',
      rewards: '{{count}} {{name}} restored for {{fullName}}.',
      newTotalSingle: '{{firstName}} now has 1 available reward.',
      newTotal: '{{firstName}} now has {{count}} available rewards.',
      scanNext: 'Scan next',
      done: 'Done',
    },
  },
  stampCorrection: {
    title: 'Remove stamps',
    sheetBackdropAccessibilityLabel: 'Close stamp removal',
    disclaimer: 'This action creates a correction record. Original activity remains unchanged.',
    removeButton: 'Remove stamps',
    reason: {
      label: 'Reason for removal',
      placeholder: 'Select a reason',
    },
    note: 'Note (optional)',
    notePlaceholder: 'Add a note about this removal...',
    stepper: {
      label: 'Stamps to remove',
      stampZero: '0 stamps',
      stampSingle: '- 1 stamp',
      stamps: '- {{count}} stamps',
      decrementAccessibilityLabel: 'Remove fewer stamps',
      incrementAccessibilityLabel: 'Remove more stamps',
      countAccessibilityLabel: 'Removing {{count}} stamps',
    },
    menu: {
      removeStamp: 'Remove stamp',
      restoreReward: 'Restore reward',
      closeAccessibilityLabel: 'Close member menu',
    },
    success: {
      title: 'Stamps removed',
      stampsSingle: '1 stamp removed from {{fullName}}.',
      stamps: '{{count}} stamps removed from {{fullName}}.',
      newTotalPrefix: '{{firstName}} now has',
      newTotalSuffix: 'stamps.',
      scanNext: 'Scan next',
      done: 'Done',
    },
    managerPin: {
      title: 'Manager PIN',
      subtitle: 'Enter manager PIN to remove stamps.',
      modalAccessibilityLabel: 'Manager PIN authorisation',
      closeAccessibilityLabel: 'Cancel PIN entry',
      codeProgressAccessibilityLabel: '{{entered}} of {{total}} digits entered',
      incorrectPin: 'Incorrect PIN. Please try again.',
      removeError: 'Could not remove stamps. Please try again.',
    },
  },
  stampAwarding: {
    sheetBackdropAccessibilityLabel: 'Close member stamp card',
    memberFound: 'Member found',
    memberMenuAccessibilityLabel: 'Member options',
    unknownMember: 'Unknown member',
    stampCountSingle: '1 stamp',
    stampCount: '{{count}} stamps',
    addStampsSingle: 'Add 1 stamp',
    addStamps: 'Add {{count}} stamps',
    addStampsAction: 'Add stamps',
    thisWillAdd: 'This will add',
    maxStampsCapped: 'Capped at the maximum of {{max}} stamps per transaction.',
    loadError: 'Could not load member data. Please try again.',
    receiptReference: {
      label: 'Receipt reference',
      placeholder: 'Enter receipt or order reference',
    },
    perMeal: {
      label: 'Number of stamps',
      decrementAccessibilityLabel: 'Decrease stamp count',
      incrementAccessibilityLabel: 'Increase stamp count',
      countAccessibilityLabel: '{{count}} stamps selected',
    },
    spendBased: {
      label: 'Purchase amount',
      placeholder: '0',
      currencyLabel: 'SEK',
      earningRule: 'Earning rule: {{amount}} SEK = 1 stamp',
      amountAccessibilityLabel: 'Purchase amount in SEK',
    },
    stampPreview: {
      label: 'Stamp preview',
      current: 'Current: {{current}} / {{target}}',
      currentLabel: 'Before',
      afterThis: 'After this: {{after}} / {{target}}',
      afterThisLabel: 'After',
      rewardUnlocked: 'Free Meal unlocked',
      rewardsUnlockedCount: '{{count}} Free Meals unlocked',
      nextCardStarts: 'Next card starts at {{carry}} / {{target}}',
      nextCardStartsLabel: 'Next card starts at',
      stampFilledAccessibilityLabel: 'Stamp collected',
      stampPreviewAccessibilityLabel: 'Stamp to be added',
      stampEmptyAccessibilityLabel: 'Empty stamp slot',
    },
    availableRewards: {
      title: 'Available rewards',
      redemptionNote: 'Reward redemption requires customer QR.',
      rewardAccessibilityLabel: '{{name}} — reward available',
    },
    recentActivity: {
      title: 'Recent activity',
      activityType: {
        stamps_added:     'Stamps added',
        stamp_correction: 'Stamp correction',
        reward_unlocked:  'Reward unlocked',
        reward_redeemed:  'Reward redeemed',
        reward_restored:  'Reward restored',
        referral_bonus:   'Referral bonus',
        reward_expired:   'Reward expired',
      },
      stampSingle:  'stamp',
      stamps:       'stamps',
      rewardSingle: 'reward',
      rewards:      'rewards',
    },
    staffPin: {
      title: 'Cashier PIN',
      subtitle: 'Enter cashier PIN to add stamps',
      modalAccessibilityLabel: 'Cashier PIN authorisation',
      closeAccessibilityLabel: 'Cancel PIN entry',
      codeProgressAccessibilityLabel: '{{entered}} of {{total}} digits entered',
      incorrectPin: 'Incorrect PIN. Please try again.',
      awardError: 'Could not add stamps. Please try again.',
    },
    success: {
      title: 'Stamps added',
      stampsSingle: '1 stamp added for {{fullName}}.',
      stamps: '{{count}} stamps added for {{fullName}}.',
      newTotalPrefix: '{{firstName}} now has',
      newTotalSuffix: 'stamps.',
      rewardUnlocked: '{{rewardName}} unlocked!',
      rewardsUnlocked: '{{count}}x {{rewardName}} unlocked!',
      scanNext: 'Scan next',
      done: 'Done',
    },
  },
  loyalty: {
    saveChanges: 'Save changes',
    loadError: 'Could not load the loyalty program configuration.',
    saveConfirm: {
      title: 'Confirm changes',
      subtitle: 'Review the changes below before saving.',
      closeAccessibilityLabel: 'Close confirmation',
      confirm: 'Confirm',
      saving: 'Saving...',
      saveError: 'Could not save the loyalty configuration. Please try again.',
      completionMilestone: 'Completion milestone',
      completionRewardInfo: 'Only affects future card completions. Already awarded rewards are unchanged.',
      stampTargetDecreaseWarning_one: '{{count}} customer will complete their card automatically. Extra stamps carry over.',
      stampTargetDecreaseWarning_other: '{{count}} customers will complete their card automatically. Extra stamps carry over.',
    },
    earningRule: {
      sectionTitle: 'Earning rule',
      subtitle: 'Choose how customers earn stamps.',
      perMeal: 'Per meal',
      perMealDescription: 'Customers earn 1 stamp for each eligible meal purchased.',
      spendBased: 'Spend based',
      spendBasedDescription: 'Customers earn stamps based on how much they spend.',
      amountPerStamp: 'Amount needed for 1 stamp',
      currency: 'SEK',
      exampleSingular: 'Example: {{amount}} SEK spent earns 1 stamp.',
      example: 'Example: {{amount}} SEK spent earns {{stamps}} stamps.',
      amountRequiredError: 'Enter an amount',
      amountIntegerError: 'Enter a whole number',
      amountRangeError: 'Enter an amount between {{min}} and {{max}} SEK',
    },
    rewardItems: {
      sectionTitle: 'Reward items',
      subtitle: 'Create and manage the rewards you can assign to milestones.',
      add: 'Add reward item',
      addDescription: 'Create a new reward your customers can earn.',
      active: 'Active',
      inactive: 'Inactive',
      translationIncomplete: 'Translation',
      loadError: 'Could not load reward items.',
      modal: {
        addTitle: 'Add reward item',
        editTitle: 'Edit reward item',
        closeAccessibilityLabel: 'Close reward item',
        uploadImage: 'Upload image',
        uploadImageHint: 'JPG or PNG recommended',
        changeImage: 'Change image',
        removeImage: 'Remove image',
        contentLanguage: 'Content language',
        langEnglish: 'English',
        langSwedish: 'Swedish',
        langEnglishFull: 'English (English)',
        langSwedishFull: 'Swedish (Svenska)',
        translationsTitle: 'Translations',
        translationsStatus: '{{count}} of {{total}} complete',
        langIncomplete: '{{language}} incomplete',
        nameLabel: 'Name',
        descriptionLabel: 'Description',
        nameIncomplete: 'Add a {{language}} name to complete this translation.',
        activeLabel: 'Active',
        activeLockedHint: 'This reward is assigned to a milestone. Reassign it there before deactivating.',
        save: 'Save',
        saving: 'Saving...',
        saveError: 'Could not save this reward item. Please try again.',
      },
    },
    cardSetup: {
      sectionTitle: 'Card setup',
      subtitle: 'Set the number of stamps customers need to collect.',
      stampsPerCard: 'Stamps per card',
      description: 'Customers need {{count}} stamps to complete the card and get the completion reward.',
    },
    iconPicker: {
      drinks: 'Drinks',
      food: 'Food',
      sweets: 'Sweets & snacks',
      reward: 'Reward',
    },
    milestones: {
      sectionTitle: 'Milestones',
      subtitle: 'Define the rewards customers receive at specific stamp positions.',
      stampPosition: 'Stamp position',
      rewardItem: 'Reward item',
      cardIcon: 'Card icon',
      delete: 'Delete',
      stampLabel: 'Stamp {{position}}',
      completion: 'Completion',
      addMilestone: 'Add milestone',
      rewardItemAccessibilityLabel: 'Reward item for stamp {{position}}',
      completionRewardAccessibilityLabel: 'Completion reward item',
      deleteAccessibilityLabel: 'Delete milestone at stamp {{position}}',
      rewardItemRequiredError: 'Select a reward item',
      noRewardItems: 'Add a reward item first',
      cardIconAccessibilityLabel: 'Card icon for stamp {{position}}',
      completionCardIconAccessibilityLabel: 'Completion card icon',
    },
    customerPreview: {
      sectionTitle: 'Customer preview',
      subtitle: 'This is what customers will see.',
      logoAccessibilityLabel: 'Chill Out logo',
      cardAccessibilityLabel: 'Stamp card preview, {{current}} of {{total}} stamps collected',
      count: '{{current}} / {{total}} stamps',
      subtitleRemaining: '{{remaining}} more stamps until your next reward',
      subtitleComplete: 'Reward unlocked!',
      layoutDisclaimer: "This preview is a close approximation. The exact layout may vary slightly depending on the customer's phone screen size.",
      previewProgress: 'Preview progress',
      previewProgressDescription: 'Adjust the number of collected stamps shown in the preview.',
      decrementAccessibilityLabel: 'Decrease preview stamp count',
      incrementAccessibilityLabel: 'Increase preview stamp count',
    },
  },
  birthdayReward: {
    saveChanges: 'Save changes',
    saving: 'Saving...',
    loadError: 'Could not load the birthday reward programme.',
    saveError: 'Could not save the birthday reward programme. Please try again.',
    enable: {
      sectionTitle: 'Enable birthday reward',
      subtitle: 'Turn the birthday reward programme on or off for future issuance.',
      toggleAccessibilityLabel: 'Toggle birthday reward programme',
      enabled: 'Enabled',
      disabled: 'Disabled',
      info: 'Disabling only stops future rewards from being issued. Rewards already issued to customers are not affected.',
    },
    rewardItems: {
      sectionTitle: 'Reward item',
      subtitle: 'Create and manage the reward customers can receive during their birthday month.',
      add: 'Add reward item',
      addDescription: 'Create a new birthday reward item.',
      active: 'Active',
      inactive: 'Inactive',
      translationIncomplete: 'Translation',
      loadError: 'Could not load reward items.',
      modal: {
        addTitle: 'Add reward item',
        editTitle: 'Edit reward item',
        closeAccessibilityLabel: 'Close reward item',
        uploadImage: 'Upload image',
        uploadImageHint: 'JPG or PNG recommended',
        changeImage: 'Change image',
        removeImage: 'Remove image',
        contentLanguage: 'Content language',
        langEnglish: 'English',
        langSwedish: 'Swedish',
        translationsTitle: 'Translations',
        translationsStatus: '{{count}} of {{total}} complete',
        langIncomplete: '{{language}} incomplete',
        nameLabel: 'Name',
        descriptionLabel: 'Description',
        nameIncomplete: 'Add a {{language}} name to complete this translation.',
        activeLabel: 'Active',
        activeLockedHint: 'This reward item is selected as the active birthday reward. Choose a different one before deactivating.',
        save: 'Save',
        saving: 'Saving...',
        saveError: 'Could not save this reward item. Please try again.',
      },
    },
    activeItem: {
      sectionTitle: 'Select active reward',
      subtitle: 'Choose which reward item is currently issued.',
      placeholder: 'Select a reward item',
      noRewardItems: 'Add a reward item first',
      requiredError: 'Select a reward item',
    },
    howItWorks: {
      sectionTitle: 'How it works',
      notification: {
        title: 'Birthday notification',
        body: "Customers receive a notification when their birthday reward becomes available.",
      },
      validMonth: {
        title: 'Valid during birthday month',
        body: "The reward is valid from the start to the end of the customer's birthday month.",
      },
      oncePerYear: {
        title: 'Issued once a year',
        body: "The reward is issued automatically once, during the customer's birthday month, each year.",
      },
      birthdaySaved: {
        title: 'Birthday must be saved',
        body: 'Customers must save their birthday to be eligible. There is no need to do so before their birthday month begins.',
      },
    },
  },
  auditLog: {
    export: 'Export CSV',
    exporting: 'Exporting...',
    exportError: 'Unable to export. Please try again.',
    exportAccessibilityLabel: 'Export audit log as CSV',
    filters: {
      label: 'Filter',
      clear: 'Clear filters',
      action: 'Action',
      admin: 'Admin',
    },
    dateRange: {
      closeAccessibilityLabel: 'Close date picker',
      today: 'Today',
      yesterday: 'Yesterday',
      last7Days: 'Last 7 days',
      last30Days: 'Last 30 days',
      last90Days: 'Last 90 days',
    },
    eventGroups: {
      cashierMgmt: 'Cashier management',
      deviceMgmt: 'Device management',
      settings: 'Settings',
      loyalty: 'Loyalty',
      gdpr: 'GDPR',
      adminAuth: 'Admin authentication',
    },
    eventTypes: {
      cashier_created: 'Cashier created',
      cashier_updated: 'Cashier updated',
      device_created: 'Device created',
      device_updated: 'Device updated',
      loyalty_created: 'Loyalty created',
      loyalty_updated: 'Loyalty updated',
      reward_item_created: 'Reward item created',
      reward_item_updated: 'Reward item updated',
      settings_updated: 'Settings updated',
      member_anonymised: 'Member anonymised',
      stamp_adjustment: 'Stamp adjustment',
      reward_adjustment: 'Reward adjustment',
      admin_signed_in: 'Admin signed in',
      admin_signed_out: 'Admin signed out',
      password_reset_requested: 'Password reset requested',
      password_changed: 'Password changed',
      admin_sign_in_failed: 'Admin sign-in failed',
      birthday_reward_updated: 'Birthday reward updated',
    },
    columns: {
      dateTime: 'Date & time',
      action: 'Action',
      target: 'Target',
      details: 'Details',
      actionBy: 'Action by',
    },
    emptyTitle: 'No audit log entries',
    emptyDescription: 'Audit log entries will appear here as admin actions are recorded.',
    loadError: 'Could not load the audit log. Please try again.',
    noResults: 'No entries match the selected filters.',
    rowAccessibilityLabel: '{{action}} by {{admin}} at {{time}}',
    detail: {
      title: 'Audit log entry',
      closeAccessibilityLabel: 'Close audit log entry',
      done: 'Done',
      timestampLabel: 'Timestamp',
      actionByLabel: 'Action by',
      actionLabel: 'Action',
      targetLabel: 'Target',
      changesLabel: 'Changes',
      reasonLabel: 'Reason',
      noteLabel: 'Note',
      ipAddressLabel: 'IP address',
      userAgentLabel: 'User agent',
      beforeLabel: 'Before',
      afterLabel: 'After',
      noChanges: 'No changes recorded',
    },
    csv: {
      filterDateRange: 'Date range',
    },
  },
  recentActivity: {
    export: 'Export CSV',
    exporting: 'Exporting...',
    exportError: 'Unable to export. Please try again.',
    exportAccessibilityLabel: 'Export recent activity as CSV',
    search: {
      label: 'Search members',
      placeholder: 'Search by member name, ID or email',
      accessibilityLabel: 'Search recent activity by member',
      clearAccessibilityLabel: 'Clear search',
    },
    filters: {
      label: 'Filter',
      clear: 'Clear filters',
      activityType: 'Activity type',
      staff: 'Performed by',
      memberStatus: 'Member status',
      anonymised: 'Anonymised',
      active: 'Active',
    },
    activityTypes: {
      new_member_joined: 'New member joined',
    },
    columns: {
      dateTime: 'Date & time',
      activity: 'Activity',
      change: 'Change',
      member: 'Member',
      by: 'By',
    },
    emptyTitle: 'No activity yet',
    emptyDescription: 'Loyalty activity will appear here as members earn stamps and redeem rewards.',
    loadError: 'Could not load recent activity. Please try again.',
    noResults: 'No activity matches the selected filters.',
    rowAccessibilityLabel: '{{activity}} for {{member}} at {{time}}',
    detail: {
      closeAccessibilityLabel: 'Close activity details',
      close: 'Close',
      memberLabel: 'Member',
      performedByLabel: 'Performed by',
      changeSummaryLabel: 'Change summary',
      receiptLabel: 'Receipt reference',
      reasonLabel: 'Reason',
      noteLabel: 'Note',
      deviceLabel: 'Device',
      system: 'System',
      managerRole: 'Manager',
      adminRole: 'Admin',
      targetReductionLabel: 'Stamp target reduced',
      targetReductionStampsPerCard: 'Stamps per card: {{targetBefore}} → {{targetAfter}}',
      targetReductionMemberStamps: 'Member\'s stamps: {{before}} → {{after}}',
    },
    csv: {
      filterDateRange: 'Date range',
      timestamp: 'Timestamp',
      activity: 'Activity',
      delta: 'Change',
      member: 'Member',
      memberEmail: 'Member email',
      memberId: 'Member ID',
      performedBy: 'Performed by',
      device: 'Device',
      receipt: 'Receipt reference',
      reason: 'Reason',
      note: 'Note',
    },
  },
  settings: {
    saveChanges: 'Save changes',
    programOptions: {
      title: 'Program options',
      subtitle: 'Manage settings that control stamp usage and customer growth.',
    },
    maxStampsPerTransaction: {
      title: 'Maximum stamps per transaction',
      description: 'Cap the number of stamps that can be added in a single transaction. Range: 1–999.',
      decrementAccessibilityLabel: 'Decrease maximum stamps',
      incrementAccessibilityLabel: 'Increase maximum stamps',
    },
    receiptReference: {
      title: 'Receipt / order reference',
      description: 'Require a receipt or order reference when adding stamps.',
      accessibilityLabel: 'Toggle receipt reference requirement',
    },
    referralProgram: {
      title: 'Referral program',
      description: 'Allow members to refer friends. Both the referrer and the invited friend earn 1 stamp each.',
      accessibilityLabel: 'Toggle referral program',
    },
    cashierPermissions: {
      title: 'Cashier permissions',
      subtitle: 'Control which adjustment actions cashiers can perform.',
    },
    cashierStampCorrection: {
      title: 'Stamp correction',
      description: 'Allow cashiers to remove stamps with a manager PIN.',
      accessibilityLabel: 'Toggle cashier stamp correction',
    },
    cashierRewardRestore: {
      title: 'Reward restore',
      description: 'Allow cashiers to restore wrongly-redeemed rewards with a manager PIN.',
      accessibilityLabel: 'Toggle cashier reward restore',
    },
    discardChanges: {
      title: 'Discard unsaved changes?',
      body: 'Your changes have not been saved.',
      keepEditing: 'Keep editing',
      discard: 'Discard changes',
      closeAccessibilityLabel: 'Keep editing',
    },
    saveConfirm: {
      title: 'Save changes?',
      subtitle: 'Review your changes before saving.',
      confirm: 'Save changes',
      saving: 'Saving...',
      saveError: 'Failed to save settings. Please try again.',
      maxStampsPerTransaction: 'Max stamps per transaction',
      receiptReference: 'Receipt / order reference',
      referralProgram: 'Referral program',
      cashierStampCorrection: 'Cashier stamp correction',
      cashierRewardRestore: 'Cashier reward restore',
    },
  },
}

export default en
export type Translations = typeof en
